import React, { useEffect, useRef, useState } from "react";
import type { IUserCuratorData } from "redux/users/types";
import { useTranslation } from "react-i18next";
import { ComponentsLoader } from 'components';
import { ReactComponent as ChatIcon } from "assets/icons/new/Profile/Support.svg";
import { ReactComponent as ShowListIcon } from "assets/icons/new/UI/ShowList.svg";
import { ReactComponent as CopyIcon } from "assets/icons/new/UI/CopyValue.svg";
import { ReactComponent as DocsIcon } from "assets/icons/new/Profile/Docs.svg";
import { ReactComponent as CallIcon } from "assets/icons/new/Profile/Call.svg";
import { ReactComponent as ChatMenuIcon } from "assets/icons/new/Profile/Chat.svg";

interface IChatMenuProps extends IUserCuratorData {
  chatMenuOpened: boolean,
  sidebarOpen: boolean,
  openChatHandler: (data: boolean) => void,
  setChatMenuOpened: (data: boolean) => void,
  userShortId?: string;
  hideMdElements?: boolean,
}

const ChatMenu: React.FC<IChatMenuProps> = ({ openChatHandler, userShortId, sidebarOpen, isLoaded, hideMdElements}) => {
  const [chatMenuOpened, setChatMenuOpened] = useState(false);
  const { t } = useTranslation()
  const menu = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const clickHandler = (e: Event) => {
      if(e.composedPath().some((item: any) => item.id === "dropdown-open")) return;
      if(!e.composedPath().includes(menu.current as any)) {
        setChatMenuOpened(false);
      }
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, []);

  return (
    <div className='relative h-full [--strategy:static] md:[--strategy:absolute] [--adaptive:static] md:[--adaptive:absolute]'>
      <div id="dropdown-open" className='justify-center cursor-pointer'>
        {
          !isLoaded ?
            <ComponentsLoader size={30} />
            :
            <div
              onClick={() => setChatMenuOpened(prev => !prev)}
              id="chatMenuButton"
              className={`flex items-center justify-between hover:text-theme-hover dark:hover:text-theme-hover`}
            >
              <div className={`flex items-center min-w-0 ${!hideMdElements ? 'sm:gap-x-3 max-sm:gap-x-3' : ''}`}>
                <div className="shrink-0">
                  <ChatIcon width="20" height="20" fill="currentColor" />
                </div>

                <div className={`transition-all duration-300 overflow-hidden ${sidebarOpen ? 'lg:opacity-100' : 'lg:opacity-0'}`}>
                  <button
                    style={{outline: "none"}}
                    className={`text-[13px] whitespace-nowrap ${hideMdElements ? 'hidden lg:block maxh620:hidden' : ''}`}
                  >
                    {t("Chat support")}
                  </button>
                </div>
              </div>

              <div className="shrink-0">
                <ShowListIcon
                  width="24"
                  height="24"
                  fill="currentColor"
                  className={`size-3.5 transition-all duration-300 ${hideMdElements ? 'hidden lg:block maxh620:hidden' : ''} ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`}
                />
              </div>
            </div>
        }
      </div>
      {chatMenuOpened && (
        <div
          className={`z-10 h-41 text-[13px] lg:mx-[9px] absolute lg:bottom-0 sm:top-full max-sm:top-[calc(100%+7px)] right-0 ${sidebarOpen ? 'lg:left-[185px]' : 'lg:left-[33px]'} w-42 shadow-2xl border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg rounded-lg overflow-hidden flex flex-col`}
          ref={menu}
        >
          <div className='flex flex-col items-center w-full cursor-pointer'>
            <a
              className={`w-full rounded-t-[6px] text-left py-2 px-3 flex items-center gap-x-2 hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active`}
              rel="noreferrer"
              href="https://lk.market/ru/help/"
              target="_blank"
            >
              <DocsIcon width="16" height="16" fill="currentColor" className="shrink-0" />
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">{t('tips')}</span>
            </a>
            <a className={`w-full text-left py-2 px-3 flex items-center gap-x-2 hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active`} href="tel:+74951451000">
              <CallIcon width="16" height="16" fill="currentColor" className="shrink-0" />
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">{t('call')}</span>
            </a>
            <button className={`w-full text-left py-2 px-3 flex items-center gap-x-2 hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active`} onClick={() => openChatHandler(true)}>
              <ChatMenuIcon width="16" height="16" fill="currentColor" className="shrink-0" />
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">{t('chat')}</span>
            </button>
          </div>
          <div
            className={`flex-1 border-t border-theme-border dark:border-theme-border cursor-copy hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active`}
            onClick={() => userShortId && navigator.clipboard.writeText(String(userShortId))}
          >
            <div className="py-2 px-3 flex items-center gap-x-1">
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">{t('pin')}: {userShortId}</span>
              <CopyIcon width="14" height="14" fill="currentColor" className="shrink-0" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatMenu;